import { useRouter as useNextRouter } from 'next/navigation';
import { useCallback } from 'react';

import { buildRoute, ExtractRouteParams, RouteParams } from '../../routing';
import {
  notifyRoutingContextChanged,
  useRoutingContext,
} from './use-routing-context';

interface BaseNavigationOptions<T = unknown> {
  scroll?: boolean;
  state?: T;
}

interface NavigateOptionsWithoutParams<T = unknown>
  extends BaseNavigationOptions<T> {
  query?: Record<string, QueryParamValue | QueryParamValue[]>;
}

interface NavigateOptionsWithParams<R extends string, T = unknown>
  extends NavigateOptionsWithoutParams<T> {
  params: RouteParams<R>;
}

type NavigateOptions<R extends string, T = unknown> =
  ExtractRouteParams<R> extends never
    ? NavigateOptionsWithoutParams<T>
    : NavigateOptionsWithParams<R, T>;

type QueryParamValue = string | number | boolean | object;

type QueryParamOperator = 'equals' | 'starts-with';

const normalizeQueryParamValue = (value: QueryParamValue) =>
  typeof value === 'object' ? JSON.stringify(value) : value.toString();

const excludedValues: unknown[] = ['', undefined, null];

/**
 * Custom hook to use the router with additional navigation and query parameter manipulation capabilities.
 * @function useRouter
 * @returns {object} An object containing router methods and additional utilities.
 */
export const useRouter = () => {
  const router = useNextRouter();
  const { routingContext } = useRoutingContext();

  /**
   * Pushes a new route to the router.
   * @function push
   * @template R - The type of the route.
   * @template T - The type of the state.
   * @param {R} href - The route to navigate to.
   * @param {NavigateOptions<R, T>} [options] - The navigation options.
   */
  const push = useCallback(
    <R extends string, T = unknown>(
      href: R,
      options?: NavigateOptions<R, T>,
    ) => {
      let route: string = href;

      if (options && 'params' in options) {
        route = buildRoute<R>(href, options.params!);
      }

      const normalized = new URLSearchParams();
      if (options?.query) {
        for (const key in options.query) {
          const value = options.query[key];

          if (Array.isArray(value)) {
            value.forEach((x) => {
              normalized.append(key, normalizeQueryParamValue(x));
            });

            continue;
          }

          if (value !== undefined) {
            normalized.append(key, normalizeQueryParamValue(value));
          }
        }

        if (normalized.toString()) {
          route = `${route}?${normalized}`;
        }
      }

      router.push(route, options);
      notifyRoutingContextChanged({
        action: 'push',
        pathname: route,
        searchParams: normalized,
        state: options?.state,
      });
    },
    [router],
  );

  /**
   * Mutates the query parameters of the current route.
   * @function mutateQueryParams
   * @param {Record<string, QueryParamValue | QueryParamValue[]>} context - The query parameters to update.
   * @param {BaseNavigationOptions & { preventNavigation?: boolean }} [options] - The navigation options.
   */
  const mutateQueryParams = useCallback(
    (
      context: Record<string, QueryParamValue | QueryParamValue[]>,
      options?: BaseNavigationOptions & {
        preventNavigation?: boolean;
      },
    ) => {
      const pathname = window.location.pathname;

      const normalized = new URLSearchParams();

      for (const [paramKey, value] of new URLSearchParams(
        window.location.search,
      )) {
        const hasUpdate = paramKey in context;
        if (hasUpdate) {
          const value = context[paramKey];

          if (excludedValues.includes(value)) continue;

          if (Array.isArray(value)) {
            value.forEach((x) => {
              normalized.append(paramKey, normalizeQueryParamValue(x));
            });

            continue;
          }

          if (value !== undefined) {
            normalized.append(paramKey, normalizeQueryParamValue(value));
          }
          continue;
        }

        if (excludedValues.includes(value)) continue;

        normalized.append(paramKey, value);
      }

      const existing = [...normalized.keys()];

      for (const key in context) {
        if (existing.includes(key)) continue;

        const value = context[key];

        if (excludedValues.includes(value)) continue;

        if (Array.isArray(value)) {
          value.forEach((x) => {
            normalized.append(key, normalizeQueryParamValue(x));
          });

          continue;
        }

        if (value !== undefined) {
          normalized.append(key, normalizeQueryParamValue(value));
        }
      }

      const route = `${pathname}?${normalized}`;

      if (options?.preventNavigation) {
        window.history.replaceState({}, '', route);
        return;
      }

      push(route, options);
    },
    [push],
  );

  /**
   * Removes specific query parameters from the current route.
   * @function removeQueryParams
   * @param {string} key - The key of the query parameter to remove.
   * @param {BaseNavigationOptions & { operator?: QueryParamOperator, preventNavigation?: boolean }} [options] - The navigation options.
   */
  const removeQueryParams = useCallback(
    (
      key: string,
      options?: BaseNavigationOptions & {
        operator?: QueryParamOperator;
        preventNavigation?: boolean;
      },
    ) => {
      const pathname = window.location.pathname;

      const operator = options?.operator || 'equals';

      const normalized = new URLSearchParams();

      for (const [paramKey, value] of new URLSearchParams(
        window.location.search,
      )) {
        const matchEquals = operator === 'equals' && paramKey === key;
        const matchStartswith =
          operator === 'starts-with' && paramKey.startsWith(key);

        if (matchEquals || matchStartswith) continue;

        normalized.append(paramKey, value);
      }

      const route = `${pathname}?${normalized}`;

      if (options?.preventNavigation) {
        window.history.replaceState({}, '', route);
        return;
      }

      push(route, options);
    },
    [push],
  );

  /**
   * Clears all query parameters from the current route.
   * @function clearQueryParams
   * @param {BaseNavigationOptions & { preventNavigation?: boolean }} [options] - The navigation options.
   */
  const clearQueryParams = useCallback(
    (
      options?: BaseNavigationOptions & {
        preventNavigation?: boolean;
      },
    ) => {
      const pathname = window.location.pathname;

      if (options?.preventNavigation) {
        window.history.replaceState({}, '', pathname);
        return;
      }

      push(pathname, options);
    },
    [push],
  );

  return {
    ...router,
    /**
     * Navigates back to the previous route.
     * @function back
     * @template T - The type of the state.
     * @param {BaseNavigationOptions<T>} [options] - The navigation options.
     */
    back: <T = unknown>(options?: BaseNavigationOptions<T>) => {
      router.back();
      notifyRoutingContextChanged({
        action: 'pop',
        pathname: window.location.pathname,
        searchParams: new URLSearchParams(window.location.search),
        state: options?.state,
      });
    },
    /**
     * Replaces the current route with a new one.
     * @function replace
     * @template R - The type of the route.
     * @template T - The type of the state.
     * @param {R} href - The route to navigate to.
     * @param {NavigateOptions<R, T>} [options] - The navigation options.
     */
    replace: <R extends string, T = unknown>(
      href: R,
      options?: NavigateOptions<R, T>,
    ) => {
      router.replace(href, options);
      notifyRoutingContextChanged({
        action: 'replace',
        pathname: window.location.pathname,
        searchParams: new URLSearchParams(window.location.search),
        state: options?.state,
      });
    },
    push,
    mutateQueryParams,
    removeQueryParams,
    clearQueryParams,
    routingContext,
  };
};
